import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Button
} from "@mui/material";
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

function TranslationPhraseEditorGuide({ open, onClose }) {
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Phrases Editor User Guide</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" color="primary">
                            Understanding Phrase Detection & Mapping
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="1. Detected Phrases"
                                    secondary="Phrases are automatically detected when you 'Split into words'. They appear colored in the text and in the left panel"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="2. Color Coding"
                                    secondary="Each phrase gets a unique color. Words in the text are highlighted with their phrase color for easy identification"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="3. Conflict Detection"
                                    secondary="⚠️ Red borders and warning icons indicate overlapping phrases that need attention"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="4. Dictionary Mapping"
                                    secondary="Each phrase can be mapped to a dictionary entry for consistent translation across texts"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" color="secondary">
                            How to Manage Phrases
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="1. Auto-Map Phrases"
                                    secondary="Click 'Map Phrases to Dictionary' button to automatically connect phrases with existing dictionary entries"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="2. Manual Mapping"
                                    secondary="Click on any phrase in the left panel, then click a dictionary entry in the right panel to map them"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="3. Edit Translations"
                                    secondary="Use the search icon (🔍) next to translation fields to find existing translations or type new ones"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="4. Create New Phrases"
                                    secondary="Click the + button, then click words in sequence to create custom phrases"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="5. Delete Phrases"
                                    secondary="Click the delete icon (🗑️) to remove phrases or dictionary entries"
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" color="info.main">
                            Workflow Best Practices
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary="1. Start with Auto-Mapping"
                                    secondary="Always click 'Map Phrases to Dictionary' first to connect with existing translations"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="2. Review Conflicts"
                                    secondary="Check for red-bordered phrases and resolve overlapping positions manually"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="3. Focus on Unmapped Phrases"
                                    secondary="Prioritize phrases showing 'Click to map to dictionary' - these need attention"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="4. Use Search Function"
                                    secondary="When adding translations, use the search feature to find existing phrase translations"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary="⚠️ After Text Edits"
                                    secondary="When text is re-split, phrase positions change. Always re-check phrase mappings and resolve new conflicts"
                                    primaryTypographyProps={{ color: 'warning.main', fontWeight: 'bold' }}
                                />
                            </ListItem>
                        </List>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default TranslationPhraseEditorGuide; 